'use client'

import { analytics } from '@/core/analytics/google-analytics'
import { fbp } from '@/core/analytics/pixel'
import { PROVIDER_ROUTES, ROUTES } from '@/core/constants/routes'
import { useMutation } from '@/core/hooks/use-mutation'
import { safeParseFloat } from '@/core/lib/utils/parse-float'
import { useAuth } from '@/core/store/auth'
import { ID } from '@/core/types/models/id.model'
import { getTransferValidation } from '@/features/reservation/form-validation/transfer.validation'
import { useReservationStore } from '@/features/reservation/store'
import { PaymentMethod } from '@/features/reservation/types/enums/payment-method.enum'
import { CreatedTripModel } from '@/features/reservation/types/models/created-trip.model'
import { TripDetailsModel } from '@/features/trips/types/models/trip-details.model'
import { useRouter } from '@/i18n/navigation'
import { addToast } from '@heroui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { askForPayment, confirmPayment } from '../../providers/client'
import { AskForPaymentModel } from '../../types/models/ask-for-payment.model'
import { ConfirmPaymentModel } from '../../types/models/confirm-payment.model'
import { PaymentDetailsModel } from '../../types/models/payment-details.model'
import { usePaymentStrategy } from './payment-strategies/use-payment-strategy'

const parsePrice = (price?: string | number, fallback?: number) =>
  safeParseFloat(price || '') || fallback || 0

const calculatePriceDifference = (
  tripToUpdate: TripDetailsModel | null,
  createdTrip: CreatedTripModel | null,
): number => {
  let oldPrice = 0

  if (tripToUpdate) {
    oldPrice = parsePrice(tripToUpdate.providerPrice, tripToUpdate.totalPrice)
  }

  const newPrice = parsePrice(
    createdTrip?.providerPrice,
    createdTrip?.totalPrice,
  )

  return tripToUpdate ? newPrice - oldPrice : newPrice
}

type Params = { tripId: ID; isProvider?: boolean }

export function useTransferForm({ tripId, isProvider = false }: Params) {
  const t = useTranslations()
  const router = useRouter()
  const { user, revalidateUser } = useAuth()
  const {
    createdTrip,
    tripToUpdate,
    paymentDetails,
    setPaymentDetails,
    promoCodePrice,
  } = useReservationStore()
  const { schema } = getTransferValidation(t, 0)

  const onPaymentSuccess = () => {
    revalidateUser()
    fbp.trackCustom('TripPaid', {
      amount:
        (paymentDetails?.amountFromBank || 0) +
        (paymentDetails?.amountFromWallet || 0) +
        (paymentDetails?.amountFromPaypal || 0),
      currencyName: createdTrip?.currency.name,
      transactionId: paymentDetails?.transactionId || '',
      tripId: createdTrip?.id.toString() || '',
    })
    analytics.trackEvent('trip_paid', {
      amount:
        (paymentDetails?.amountFromBank || 0) +
        (paymentDetails?.amountFromWallet || 0) +
        (paymentDetails?.amountFromPaypal || 0),
      currencyName: createdTrip?.currency.name,
      transactionId: paymentDetails?.transactionId || '',
      tripId: createdTrip?.id.toString() || '',
    })

    if (tripToUpdate) {
      router.push(
        isProvider
          ? PROVIDER_ROUTES.updateTripPaymentSucceed(tripId)
          : ROUTES.updateTripPaymentSucceed(tripId),
      )
    } else {
      router.push(
        isProvider
          ? PROVIDER_ROUTES.reservationPaymentSucceed(tripId)
          : ROUTES.tripPaymentSucceed(tripId),
      )
    }
  }
  const onPaymentFail = (message: string) => {
    fbp.trackCustom('TripPaymentError', {
      message,
      transactionId: paymentDetails?.transactionId || '',
      tripId: createdTrip?.id.toString() || '',
    })
    if (tripToUpdate) {
      router.push(
        isProvider
          ? PROVIDER_ROUTES.updateTripPaymentFailed(tripId)
          : ROUTES.updateTripPaymentFailed(tripId),
      )
    } else {
      router.push(
        isProvider
          ? PROVIDER_ROUTES.reservationPaymentFailed(tripId)
          : ROUTES.tripPaymentFailed(tripId),
      )
    }
  }

  const { mutate: handleAskForPayment, isPending: isAskForPaymentPending } =
    useMutation<AskForPaymentModel, PaymentDetailsModel>({
      mutation: data => askForPayment(data, !!tripToUpdate),
      onSuccess(paymentDetails) {
        setPaymentDetails(paymentDetails)

        fbp.trackCustom('PaymentInitiated', {
          amount:
            (paymentDetails?.amountFromBank || 0) +
            (paymentDetails?.amountFromWallet || 0) +
            (paymentDetails?.amountFromPaypal || 0),
          currencyName: createdTrip?.currency.name,
          transactionId: paymentDetails?.transactionId || '',
          tripId: createdTrip?.id.toString() || '',
        })
        analytics.trackEvent('payment_initiated', {
          amount:
            (paymentDetails?.amountFromBank || 0) +
            (paymentDetails?.amountFromWallet || 0) +
            (paymentDetails?.amountFromPaypal || 0),
          currencyName: createdTrip?.currency.name,
          transactionId: paymentDetails?.transactionId || '',
          tripId: createdTrip?.id.toString() || '',
        })
      },
      onError(error) {
        fbp.trackCustom('PaymentInitiateError', {
          message: error || '',
          tripId: createdTrip?.id.toString() || '',
        })
        analytics.trackEvent('payment_initiate_error', {
          message: error || '',
          tripId: createdTrip?.id.toString() || '',
        })

        addToast({
          color: 'danger',
          title: error,
        })
      },
    })

  const { mutate: confirmPaymentMutation, isPending: isConfirmPaymentPending } =
    useMutation<ConfirmPaymentModel>({
      mutation: data => confirmPayment(data, !!tripToUpdate),
      onError: error => {
        onPaymentFail(error || t('trip.paymentErr.msg1'))
      },
      onSuccess: onPaymentSuccess,
    })

  const form = useForm<AskForPaymentModel>({
    defaultValues: {
      paymentMethod:
        user && user.wallet > 0
          ? PaymentMethod.WALLET
          : PaymentMethod.BANK_CARD,
      tripId,
      termsAccepted: !!user,
    },
    resolver: zodResolver(schema),
  })

  const paymentMethod = form.watch('paymentMethod')
  const walletIncluded = [
    PaymentMethod.WALLET,
    PaymentMethod.WALLET_BANK_CARD,
    PaymentMethod.WALLET_PAYPAL,
  ].includes(paymentMethod)

  const priceDifference = calculatePriceDifference(tripToUpdate, createdTrip)

  const shouldShowPaymentForm = Boolean(
    paymentDetails?.amountFromBank &&
      paymentDetails.amountFromBank > 0 &&
      (paymentMethod === PaymentMethod.BANK_CARD ||
        paymentMethod === PaymentMethod.WALLET_BANK_CARD),
  )
  const shouldShowPaypalForm = Boolean(
    paymentDetails?.amountFromPaypal &&
      paymentDetails.amountFromPaypal > 0 &&
      (paymentMethod === PaymentMethod.PAYPAL ||
        paymentMethod === PaymentMethod.WALLET_PAYPAL),
  )
  const shouldShowRestForm = Boolean(
    ((paymentDetails?.amountFromBank &&
      paymentDetails.amountFromBank > 0 &&
      paymentMethod === PaymentMethod.WALLET) ||
      walletIncluded) &&
      (createdTrip?.totalPrice || 0) > (user?.wallet || 0),
  )

  const shouldConfirmWithWallet =
    Boolean(
      paymentDetails?.amountFromWallet &&
        paymentDetails.amountFromWallet > 0 &&
        paymentMethod === PaymentMethod.WALLET,
    ) ||
    (paymentDetails?.amountFromWallet === 0 &&
      paymentDetails?.amountFromBank === 0 &&
      paymentDetails?.amountFromPaypal === 0)

  const {
    Button: paymentButton,
    Form: paymentForm,
    Provider: PaymentProvider,
  } = usePaymentStrategy({
    shouldShowPaymentForm,
    createdTrip,
    isAskForPaymentPending,
    isConfirmPaymentPending,
    onPaymentFail,
    onPaymentSuccess,
    paymentDetails,
    shouldConfirmWithWallet,
    shouldShowPaypalForm,
    currency: createdTrip?.currency.code,
  })

  useEffect(() => {
    setPaymentDetails(null)
  }, [setPaymentDetails])

  useEffect(() => {
    if (shouldShowRestForm) {
      form.setValue('paymentMethod', PaymentMethod.WALLET_BANK_CARD)
    }
  }, [shouldShowRestForm, form])

  useEffect(() => {
    if (!createdTrip) {
      router.push(ROUTES.home)
    }
  }, [createdTrip, router])

  const handleConfirmPayment = () => {
    confirmPaymentMutation({
      transactionId: paymentDetails?.transactionId,
      tripId,
    })
  }

  const totalPrice =
    promoCodePrice ||
    priceDifference ||
    createdTrip?.providerPrice ||
    createdTrip?.totalPrice

  return {
    form,
    handleAskForPayment,
    handleConfirmPayment,
    priceDifference,
    shouldShowRestForm,
    shouldConfirmWithWallet,
    walletIncluded,
    user,
    createdTrip,
    tripToUpdate,
    setPaymentDetails,
    paymentDetails,
    paymentButton,
    paymentForm,
    PaymentProvider,
    onPaymentSuccess,
    onPaymentFail,
    totalPrice,
  }
}
